import "./index.css";
import {
  initBv,
  bvApi,
  type BvSession,
  mountShell,
  statRow,
  dataTable,
  card,
  emptyState,
  pill,
  openModal,
  flash,
  fmtMoney,
  skeletonCard,
  h,
} from "./bv-init";

interface Config {
  slug: string;
  business_name: string;
  wa_number: string | null;
  greeting: string;
  currency: string;
  published: boolean;
}
interface Product {
  id: number;
  name: string;
  price: number;
  currency: string;
  description: string | null;
}

let session: BvSession;
let currency = "JMD";
let publicUrl = "";

const digits = (s: string) => String(s || "").replace(/[^0-9]/g, "");
const waLink = (number: string | null, message: string) => {
  const n = digits(number || "");
  const text = encodeURIComponent(message);
  return n ? `https://wa.me/${n}?text=${text}` : `https://wa.me/?text=${text}`;
};

boot();

async function boot() {
  try {
    session = await initBv();
  } catch (err) {
    renderFatal(err);
    return;
  }
  currency = session.merchant.currency_code || "JMD";
  mountShell({
    brandIcon: "message",
    brandLogo: "/logo.svg",
    title: "WhatsApp Store",
    subtitle: `${session.merchant.name || "Your shop"} · order on WhatsApp`,
    poweredBy: "Marketplace",
    tabs: [
      { id: "catalog", label: "Catalog", icon: "tag", render: renderCatalog },
      { id: "settings", label: "Settings", icon: "settings", render: renderSettings },
    ],
  });
}

function renderFatal(err: unknown) {
  const root = document.getElementById("root")!;
  root.innerHTML = "";
  root.append(
    h(
      "div",
      { class: "bv-fatal" },
      h("strong", null, "Couldn't start"),
      h("p", { class: "bv-muted" }, (err as any)?.message || "No session token found."),
    ),
  );
}

async function renderCatalog(host: HTMLElement) {
  host.innerHTML = "";
  host.append(skeletonCard());
  const [ov, prods] = await Promise.all([
    bvApi<{ config: Config; products: number; public_url: string }>("/api/overview").catch(() => null),
    bvApi<{ products: Product[] }>("/api/products").catch(() => ({ products: [] as Product[] })),
  ]);
  host.innerHTML = "";
  const cfg = ov?.config;
  publicUrl = ov?.public_url || "";

  if (cfg) {
    host.append(
      statRow([
        { k: "Status", v: cfg.published ? "Open" : "Draft", icon: "message", tone: cfg.published ? "ok" : undefined },
        { k: "Products", v: String(ov!.products), icon: "tag" },
        { k: "WhatsApp", v: cfg.wa_number ? "Set" : "Not set", icon: "phone", tone: cfg.wa_number ? "ok" : "bad" },
      ]),
    );
    if (!cfg.wa_number) {
      host.append(card({ body: emptyState({ icon: "phone", title: "Add your WhatsApp number", text: "Go to Settings to add your number — orders open a chat straight to you." }) }));
    }
    const linkBody = h(
      "div",
      { class: "bv-stack" },
      h("p", { class: "bv-muted" }, cfg.published ? "Your catalog is live at:" : "Publish in Settings to make this live:"),
      h("a", { href: publicUrl, target: "_blank", rel: "noopener", style: { wordBreak: "break-all" } }, publicUrl),
      h(
        "div",
        { class: "bv-row", style: { gap: "8px", marginTop: "6px" } },
        h("button", { class: "ghost", onClick: () => copyLink(publicUrl) }, "Copy link"),
        h("a", { class: "secondary", style: { textDecoration: "none" }, href: publicUrl, target: "_blank", rel: "noopener" }, "View catalog"),
      ),
    );
    host.append(card({ title: "Public catalog", body: linkBody }));
  }

  host.append(
    card({
      title: "Products",
      action: h("button", { class: "primary", onClick: () => openProduct(host) }, "Add product"),
      body: prods.products.length
        ? dataTable<Product>({
            columns: [
              { head: "Name", cell: (p) => p.name },
              { head: "Description", cell: (p) => p.description || "—" },
              { head: "Price", num: true, cell: (p) => fmtMoney(Number(p.price), p.currency || currency) },
            ],
            rows: prods.products,
            rowActions: (p) =>
              h(
                "div",
                { class: "bv-row" },
                cfg?.wa_number
                  ? h("a", { class: "ghost", style: { textDecoration: "none" }, href: waLink(cfg.wa_number, `${cfg.greeting}\n• ${p.name} — ${fmtMoney(Number(p.price), p.currency || currency)}`), target: "_blank", rel: "noopener" }, "Test")
                  : null,
                h("button", { class: "ghost", onClick: () => openProduct(host, p) }, "Edit"),
                h("button", { class: "ghost", onClick: () => delProduct(p, host) }, "Remove"),
              ),
          })
        : emptyState({ icon: "tag", title: "No products", text: "Add the items customers can order." }),
    }),
  );
}

function openProduct(host: HTMLElement, p?: Product) {
  const name = h("input", { type: "text", value: p?.name || "" }) as HTMLInputElement;
  const price = h("input", { type: "number", min: "0", step: "0.01", value: String(p?.price ?? 0) }) as HTMLInputElement;
  const desc = h("input", { type: "text", value: p?.description || "", placeholder: "Optional" }) as HTMLInputElement;
  let close = () => {};
  const handle = openModal({
    title: p ? "Edit product" : "Add product",
    body: h("div", { class: "bv-stack" }, field("Name", name), field(`Price (${currency})`, price), field("Description", desc)),
    actions: [
      { label: "Cancel" },
      {
        label: "Save",
        primary: true,
        onClick: () => {
          if (!name.value.trim()) {
            flash("Name required", "error");
            return true;
          }
          bvApi("/api/products", { method: "POST", body: JSON.stringify({ id: p?.id, name: name.value.trim(), price: +price.value, description: desc.value.trim() || null }) })
            .then(() => {
              flash("Saved", "success");
              close();
              renderCatalog(host);
            })
            .catch((e) => flash(e?.message || "Failed", "error"));
          return true;
        },
      },
    ],
  });
  close = handle.close;
}

async function delProduct(p: Product, host: HTMLElement) {
  const ok = await bvApi(`/api/products/${p.id}/delete`, { method: "POST" }).then(() => true).catch(() => false);
  flash(ok ? `Removed ${p.name}` : "Failed", ok ? "success" : "error");
  if (ok) renderCatalog(host);
}

async function renderSettings(host: HTMLElement) {
  host.innerHTML = "";
  host.append(skeletonCard());
  const data = await bvApi<{ config: Config; public_url: string }>("/api/config").catch(() => null);
  host.innerHTML = "";
  if (!data) {
    host.append(emptyState({ icon: "alert", title: "Couldn't load settings" }));
    return;
  }
  const c = data.config;
  publicUrl = data.public_url;

  const bizName = inp(c.business_name);
  const waNumber = h("input", { type: "tel", value: c.wa_number || "", placeholder: "e.g. 18761234567 (country code, no +)" }) as HTMLInputElement;
  const greeting = h("textarea", { rows: "3", style: { width: "100%", boxSizing: "border-box" } }, c.greeting) as HTMLTextAreaElement;
  const published = h("input", { type: "checkbox" }) as HTMLInputElement;
  published.checked = c.published;

  // live preview
  const preview = h("div", { class: "bv-stack" });
  const paintPreview = () => {
    preview.innerHTML = "";
    const msg = `${greeting.value || "Hi! I would like to order:"}\n• Signature Box — ${fmtMoney(2500, currency)}`;
    const link = waLink(waNumber.value, msg);
    preview.append(
      h("div", { style: { background: "#e7f7ee", border: "1px solid #bfe6cf", borderRadius: "12px", padding: "12px", whiteSpace: "pre-wrap", fontSize: "0.9rem" } }, msg),
      h(
        "div",
        { class: "bv-row", style: { marginTop: "8px" } },
        digits(waNumber.value)
          ? h("a", { class: "secondary", style: { textDecoration: "none" }, href: link, target: "_blank", rel: "noopener" }, "Test on WhatsApp")
          : h("span", { class: "bv-muted" }, "Add a number to test"),
      ),
    );
  };
  waNumber.addEventListener("input", paintPreview);
  greeting.addEventListener("input", paintPreview);

  const save = h(
    "button",
    {
      class: "primary",
      onClick: async () => {
        const r = await bvApi<{ config: Config; public_url: string }>("/api/config", {
          method: "POST",
          body: JSON.stringify({ business_name: bizName.value.trim(), wa_number: waNumber.value.trim() || null, greeting: greeting.value.trim(), published: published.checked }),
        }).catch(() => null);
        flash(r ? "Settings saved" : "Save failed", r ? "success" : "error");
      },
    },
    "Save settings",
  );

  host.append(
    card({
      title: "Shop settings",
      action: save,
      body: h(
        "div",
        { class: "bv-stack" },
        field("Business name", bizName),
        field("WhatsApp number (with country code, digits only)", waNumber),
        field("Order message", greeting),
        h("label", { class: "bv-row" }, published, h("span", null, "Catalog published (public)")),
      ),
    }),
  );
  host.append(card({ title: "Order message preview", body: preview }));
  host.append(
    card({
      body: h(
        "p",
        { class: "bv-muted" },
        "Orders arrive as a normal WhatsApp chat using a wa.me click-to-chat link — no app install needed for customers. Native WhatsApp Business API catalogs and order webhooks are a separate, approval-gated integration.",
      ),
    }),
  );
  paintPreview();
}

function copyLink(link: string) {
  navigator.clipboard?.writeText(link).then(() => flash("Link copied", "success"), () => flash(link, "info"));
}

function inp(value: string, placeholder = ""): HTMLInputElement {
  return h("input", { type: "text", value, placeholder }) as HTMLInputElement;
}
function field(label: string, input: HTMLElement): HTMLElement {
  return h("div", { class: "bv-field" }, h("label", { class: "bv-label" }, label), input);
}
